import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { ALLERGENS } from "../data/allergens";
import { readAiTokenDenial, TOKENS_MESSAGE } from "../lib/aiTokenError";

// AllergySheetReview — photo of an allergy list → Sonnet extraction →
// MANDATORY human confirmation. AI-extracted medical data is never
// auto-applied: candidates render as removable chips with the sheet
// text that produced each one; only an explicit SAVE hands the merged
// list back to the caller (onboarding keeps it in step state; Settings
// writes it to the profile).
//
// Props:
//   open, onClose
//   existing — current allergy list (ids + custom strings)
//   onSave(nextList) — union of existing + confirmed candidates
export default function AllergySheetReview({ open, onClose, existing = [], onSave }) {
  const fileRef = useRef(null);
  const [phase, setPhase] = useState("pick"); // pick | busy | review
  const [candidates, setCandidates] = useState([]); // [{ value, label, evidence, kept }]
  const [error, setError] = useState("");

  if (!open) return null;

  const knownIds = new Set(ALLERGENS.map((a) => a.id));

  const reset = () => { setPhase("pick"); setCandidates([]); setError(""); };

  const pick = async (file) => {
    if (!file) return;
    setPhase("busy");
    setError("");
    try {
      const { compressImage } = await import("../lib/compressImage");
      // OCR needs more resolution than an avatar — mirror the
      // receipt-scan compression profile.
      const blob = await compressImage(file, { maxDimension: 1400, jpegQuality: 0.8 });
      const base64 = await new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(String(r.result).split(",")[1]);
        r.onerror = reject;
        r.readAsDataURL(blob);
      });
      const { data, error: invokeErr } = await supabase.functions.invoke("parse-allergy-sheet", {
        body: { image: base64, mediaType: "image/jpeg" },
      });
      if (invokeErr || data?.error) {
        const denial = await readAiTokenDenial(invokeErr, data);
        throw new Error(denial ? (denial.message || TOKENS_MESSAGE) : (data?.error || invokeErr?.message || "Couldn't read the sheet."));
      }
      const found = Array.isArray(data?.allergens) ? data.allergens : [];
      if (found.length === 0) {
        setError("Couldn't find any food allergens on that photo — try a clearer shot, or add them by hand.");
        setPhase("pick");
        return;
      }
      setCandidates(found.map((a) => ({
        value: a.canonicalId && knownIds.has(a.canonicalId) ? a.canonicalId : String(a.label || "").toLowerCase(),
        label: a.label,
        evidence: a.evidence || "",
        kept: true,
      })));
      setPhase("review");
    } catch (e) {
      setError(e?.message || "Couldn't read the sheet.");
      setPhase("pick");
    }
  };

  const save = () => {
    const kept = candidates.filter((c) => c.kept).map((c) => c.value);
    const merged = Array.from(new Set([...(existing || []), ...kept]));
    onSave?.(merged);
    reset();
    onClose?.();
  };

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 470,
        background: "rgba(20, 14, 8, 0.55)",
        backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "calc(env(safe-area-inset-top, 0px) + 20px) 20px calc(env(safe-area-inset-bottom, 0px) + 20px)",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) { reset(); onClose?.(); } }}
    >
      <div style={{
        width: "100%", maxWidth: 380, maxHeight: "84vh", overflowY: "auto",
        background: COLOR.night, border: `1px solid ${COLOR.border}`,
        borderRadius: 16, padding: 20,
        boxShadow: "0 18px 40px rgba(20,12,4,0.35)",
      }}>
        <div style={{ fontFamily: FONT.forma, fontSize: 16, fontWeight: 600, color: COLOR.ink, marginBottom: 6 }}>
          Import an allergy list
        </div>

        <input
          ref={fileRef}
          type="file" accept="image/*"
          onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; pick(f); }}
          style={{ display: "none" }}
        />

        {phase === "pick" && (
          <>
            <p style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.55, margin: "0 0 14px" }}>
              Snap a photo of the allergy sheet — a doctor's list, a
              school form, a note on the fridge. You'll review every
              item before anything is saved.
            </p>
            {error && (
              <div style={{
                padding: "10px 12px", marginBottom: 12,
                background: COLOR.roseDeep, border: `1px solid ${COLOR.roseDim}`,
                borderRadius: 10, fontFamily: FONT.forma, fontSize: 13, color: COLOR.rose,
              }}>
                {error}
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              className="mcm-focusable"
              style={{
                width: "100%", padding: "14px", borderRadius: 10, border: "none",
                background: COLOR.gold, color: COLOR.night,
                fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
                letterSpacing: "0.06em", cursor: "pointer",
              }}
            >
              TAKE OR CHOOSE A PHOTO
            </button>
          </>
        )}

        {phase === "busy" && (
          <p style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.55, padding: "18px 0", textAlign: "center" }}>
            Reading the sheet…
          </p>
        )}

        {phase === "review" && (
          <>
            <p style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, lineHeight: 1.55, margin: "0 0 12px" }}>
              Found these. Tap any that are wrong to drop them — then
              save. Nothing is stored until you do.
            </p>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 14 }}>
              {candidates.map((c, i) => (
                <button
                  key={`${c.value}-${i}`}
                  onClick={() => setCandidates((prev) => prev.map((x, j) => j === i ? { ...x, kept: !x.kept } : x))}
                  className="mcm-focusable"
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "10px 12px", textAlign: "left",
                    background: c.kept ? COLOR.rose : COLOR.soil,
                    border: `1px solid ${c.kept ? COLOR.rose : COLOR.border}`,
                    borderRadius: 10, cursor: "pointer",
                  }}
                >
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{
                      display: "block", fontFamily: FONT.forma, fontSize: 14, fontWeight: 700,
                      color: c.kept ? "#fff" : COLOR.sub,
                      textDecoration: c.kept ? "none" : "line-through",
                      textTransform: "capitalize",
                    }}>
                      {c.label}
                    </span>
                    {c.evidence && (
                      <span style={{
                        display: "block", marginTop: 2,
                        fontFamily: FONT.forma, fontSize: 12,
                        color: c.kept ? "rgba(255,255,255,0.75)" : COLOR.sub,
                      }}>
                        "{c.evidence}"
                      </span>
                    )}
                  </span>
                  <span style={{
                    fontFamily: FONT.forma, fontSize: 12, fontWeight: 700, flexShrink: 0,
                    color: c.kept ? "#fff" : COLOR.sub, letterSpacing: "0.06em",
                  }}>
                    {c.kept ? "KEEP" : "DROPPED"}
                  </span>
                </button>
              ))}
            </div>
            <button
              onClick={save}
              disabled={candidates.every((c) => !c.kept)}
              className="mcm-focusable"
              style={{
                width: "100%", padding: "14px", borderRadius: 10, border: "none",
                background: candidates.some((c) => c.kept) ? COLOR.gold : COLOR.soil,
                color: candidates.some((c) => c.kept) ? COLOR.night : COLOR.sub,
                fontFamily: FONT.forma, fontSize: 13, fontWeight: 700,
                letterSpacing: "0.06em",
                cursor: candidates.some((c) => c.kept) ? "pointer" : "not-allowed",
              }}
            >
              SAVE {candidates.filter((c) => c.kept).length} ALLERGEN{candidates.filter((c) => c.kept).length === 1 ? "" : "S"}
            </button>
          </>
        )}

        <button
          onClick={() => { reset(); onClose?.(); }}
          style={{
            width: "100%", marginTop: 10, background: "none", border: "none",
            padding: "8px 4px", fontFamily: FONT.forma, fontSize: 12,
            color: COLOR.muted, letterSpacing: "0.06em", cursor: "pointer",
          }}
        >
          CANCEL
        </button>
      </div>
    </div>,
    document.body,
  );
}
